// src/prototype/pages/EAWeightsPage.tsx
// ============================================================
// EA WEIGHT SETTINGS PAGE v2
// 
// Updates:
// - Smaller gutters (full width utilization)
// - Removed info banners, added HelpPopover icons
// - Manual entry + Normalize button (no auto-rebalancing)
// - Input fields next to sliders for precise control
// ============================================================

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  RotateCcw,
  ChevronDown,
  ChevronUp,
  Scale,
  Layers,
  AlertTriangle,
  Check,
} from 'lucide-react';
import { useEA } from '../contexts/EAContext';
import {
  CATEGORY_DEFINITIONS,
  MEASURE_DEFINITIONS,
  getDefaultCategoryWeightsMap,
  getDefaultMeasureWeightsMap,
} from '../utils/weighting';
// The ONLY sanctioned accessor for a measure name on screen. weighting.ts's own `label`
// bypasses the override table and rendered banned terms.
import { displayName } from '../../data/coverage/measureDisplayNames';
import { HelpPopover } from '../components/help/HelpPopover';

import type { CategoryDefinition, MeasureDefinition } from '../utils/weighting';

// ============================================================
// TYPES
// ============================================================

type TabType = 'categories' | 'measures';

interface LocationState {
  returnTo?: string;
  restoreScrollY?: number;
}

// ============================================================
// NORMALIZE UTILITY
// ============================================================

function normalizeWeights(weights: Record<string, number>): Record<string, number> {
  const total = Object.values(weights).reduce((a, b) => a + b, 0);
  if (total === 0) return weights;
  
  const normalized: Record<string, number> = {};
  let runningTotal = 0;
  const keys = Object.keys(weights);
  
  keys.forEach((key, index) => {
    if (index === keys.length - 1) {
      // Last item gets remainder to ensure exactly 100
      normalized[key] = 100 - runningTotal;
    } else {
      normalized[key] = Math.round((weights[key] / total) * 100);
      runningTotal += normalized[key];
    }
  });
  
  return normalized;
}

// ============================================================
// CATEGORY WEIGHT SLIDER COMPONENT (Manual Entry)
// ============================================================

interface CategorySliderProps {
  categoryKey: string;
  definition: CategoryDefinition;
  value: number;
  onChange: (key: string, value: number) => void;
  onReset: () => void;
  isDefault: boolean;
}

const CategorySlider: React.FC<CategorySliderProps> = ({
  categoryKey,
  definition,
  value,
  onChange,
  onReset,
  isDefault,
}) => {
  const Icon = definition.icon;
  const dimensionColor = definition.dimension === 'Business Value' 
    ? 'bg-[#18365E]/10 text-[#18365E]' 
    : 'bg-[#FF6E4C]/10 text-[#FF6E4C]';
  const dimensionBadgeColor = definition.dimension === 'Business Value'
    ? 'bg-[#18365E]/10 text-[#18365E] border-[#18365E]/20'
    : 'bg-[#FF6E4C]/10 text-[#FF6E4C] border-[#FF6E4C]/20';

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseInt(e.target.value, 10);
    if (!isNaN(newValue) && newValue >= 0 && newValue <= 100) {
      onChange(categoryKey, newValue);
    }
  };

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(categoryKey, Number(e.target.value));
  };

  return (
    <div className="p-4 bg-white rounded-lg border border-[#e5e7eb] hover:border-[#18365E]/20 transition-colors">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${dimensionColor}`}>
            <Icon className="w-4 h-4" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-[#18365E]">{definition.shortLabel}</h3>
              {!isDefault && (
                <span className="text-[9px] font-medium px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
                  Modified
                </span>
              )}
            </div>
            <p className="text-[10px] text-[#717182] mt-0.5 line-clamp-1">{definition.description}</p>
            <span className={`inline-block mt-1 text-[9px] font-medium px-2 py-0.5 rounded border ${dimensionBadgeColor}`}>
              {definition.dimension}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center border border-[#e5e7eb] rounded-lg overflow-hidden">
            <input
              type="number"
              min={0}
              max={100}
              value={Math.round(value)}
              onChange={handleInputChange}
              className="w-14 px-2 py-1.5 text-lg font-bold text-[#18365E] text-center 
                         focus:outline-none focus:ring-2 focus:ring-[#18365E]/20
                         [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
            <span className="px-2 py-1.5 bg-[#F5F7FA] text-sm text-[#717182] font-medium">%</span>
          </div>
          {!isDefault && (
            <button
              onClick={onReset}
              className="p-1.5 text-[#717182] hover:text-[#18365E] hover:bg-[#F5F7FA] rounded transition-colors"
              title="Reset to default"
            >
              <RotateCcw className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
      
      <div className="flex items-center gap-2">
        <span className="text-[10px] text-[#717182] w-5">0</span>
        <input
          type="range"
          min={0}
          max={100}
          step={1}
          value={value}
          onChange={handleSliderChange}
          className="flex-1 h-2 bg-[#e5e7eb] rounded-lg appearance-none cursor-pointer
                     [&::-webkit-slider-thumb]:appearance-none
                     [&::-webkit-slider-thumb]:w-4
                     [&::-webkit-slider-thumb]:h-4
                     [&::-webkit-slider-thumb]:rounded-full
                     [&::-webkit-slider-thumb]:bg-[#18365E]
                     [&::-webkit-slider-thumb]:cursor-pointer
                     [&::-webkit-slider-thumb]:shadow-md
                     [&::-webkit-slider-thumb]:hover:bg-[#18365E]/90
                     [&::-moz-range-thumb]:w-4
                     [&::-moz-range-thumb]:h-4
                     [&::-moz-range-thumb]:rounded-full
                     [&::-moz-range-thumb]:bg-[#18365E]
                     [&::-moz-range-thumb]:border-0
                     [&::-moz-range-thumb]:cursor-pointer"
        />
        <span className="text-[10px] text-[#717182] w-7">100</span>
      </div>
    </div>
  );
};

// ============================================================
// MEASURE WEIGHT SLIDER COMPONENT (Manual Entry)
// ============================================================

interface MeasureSliderProps {
  measureKey: string;
  definition: MeasureDefinition;
  value: number;
  onChange: (value: number) => void;
  isDefault: boolean;
}

const MeasureSlider: React.FC<MeasureSliderProps> = ({
  measureKey,
  definition,
  value,
  onChange,
  isDefault,
}) => {
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseInt(e.target.value, 10);
    if (!isNaN(newValue) && newValue >= 0 && newValue <= 100) {
      onChange(newValue);
    }
  };

  return (
    <div className="py-2.5 px-3 bg-[#F5F7FA]/50 rounded-lg hover:bg-[#F5F7FA] transition-colors">
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex-1 min-w-0 pr-3">
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-medium text-[#18365E] truncate">{displayName(definition.measureId)}</span>
            {!isDefault && (
              <span className="text-[8px] font-medium px-1 py-0.5 rounded bg-amber-100 text-amber-700 shrink-0">
                Modified
              </span>
            )}
          </div>
          <p className="text-[10px] text-[#717182] truncate">{definition.description}</p>
        </div>
        <div className="flex items-center border border-[#e5e7eb] rounded overflow-hidden bg-white">
          <input
            type="number"
            min={0}
            max={100}
            value={Math.round(value)}
            onChange={handleInputChange}
            className="w-10 px-1 py-1 text-sm font-bold text-[#18365E] text-center 
                       focus:outline-none focus:ring-1 focus:ring-[#18365E]/20
                       [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          />
          <span className="px-1.5 py-1 bg-[#F5F7FA] text-[10px] text-[#717182] font-medium">%</span>
        </div>
      </div>
      
      <input
        type="range"
        min={0}
        max={100}
        step={1}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full h-1.5 bg-[#e5e7eb] rounded-lg appearance-none cursor-pointer
                   [&::-webkit-slider-thumb]:appearance-none
                   [&::-webkit-slider-thumb]:w-3
                   [&::-webkit-slider-thumb]:h-3
                   [&::-webkit-slider-thumb]:rounded-full
                   [&::-webkit-slider-thumb]:bg-[#18365E]
                   [&::-webkit-slider-thumb]:cursor-pointer
                   [&::-webkit-slider-thumb]:shadow-sm
                   [&::-moz-range-thumb]:w-3
                   [&::-moz-range-thumb]:h-3
                   [&::-moz-range-thumb]:rounded-full
                   [&::-moz-range-thumb]:bg-[#18365E]
                   [&::-moz-range-thumb]:border-0
                   [&::-moz-range-thumb]:cursor-pointer"
      />
    </div>
  );
};

// ============================================================
// COLLAPSIBLE CATEGORY SECTION FOR MEASURES
// ============================================================

interface CategoryMeasuresSectionProps {
  categoryKey: string;
  definition: CategoryDefinition;
  measures: MeasureDefinition[];
  measureWeights: Record<string, number>;
  defaultMeasureWeights: Record<string, number>;
  onMeasureChange: (measureKey: string, value: number) => void;
  onNormalizeCategory: () => void;
  onResetCategory: () => void;
  isExpanded: boolean;
  onToggle: () => void;
}

const CategoryMeasuresSection: React.FC<CategoryMeasuresSectionProps> = ({
  categoryKey,
  definition,
  measures,
  measureWeights,
  defaultMeasureWeights,
  onMeasureChange,
  onNormalizeCategory,
  onResetCategory,
  isExpanded,
  onToggle,
}) => {
  const Icon = definition.icon;
  const hasCustomizations = measures.some(m => 
    measureWeights[m.key] !== defaultMeasureWeights[m.key]
  );
  
  const total = Object.values(measureWeights).reduce((a, b) => a + b, 0);
  const isNormalized = Math.abs(total - 100) < 0.5;

  return (
    <div className="bg-white rounded-lg border border-[#e5e7eb] overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-3 hover:bg-[#F5F7FA]/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-[#18365E]/10 flex items-center justify-center text-[#18365E]">
            <Icon className="w-4 h-4" />
          </div>
          <div className="text-left">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-[#18365E]">{definition.shortLabel}</span>
              <span className="text-[10px] text-[#717182]">({measures.length} measures)</span>
              {hasCustomizations && (
                <span className="text-[9px] font-medium px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
                  Modified
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isNormalized && isExpanded && (
            <span className="text-[10px] font-medium text-amber-600 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              {Math.round(total)}%
            </span>
          )}
          {hasCustomizations && (
            <button
              onClick={(e) => { e.stopPropagation(); onResetCategory(); }}
              className="text-[10px] font-medium text-[#717182] hover:text-[#18365E] px-2 py-1 rounded hover:bg-[#F5F7FA] transition-colors"
            >
              Reset
            </button>
          )}
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-[#717182]" />
          ) : (
            <ChevronDown className="w-5 h-5 text-[#717182]" />
          )}
        </div>
      </button>
      
      {isExpanded && (
        <div className="px-3 pb-3 space-y-2 border-t border-[#e5e7eb]">
          <div className="pt-3 pb-1 flex items-center justify-between">
            <div className="flex items-center gap-2">
              {isNormalized ? (
                <span className="text-[10px] font-medium text-green-600 flex items-center gap-1">
                  <Check className="w-3 h-3" />
                  Total: {Math.round(total)}%
                </span>
              ) : (
                <span className="text-[10px] font-medium text-amber-600 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Total: {Math.round(total)}%
                </span>
              )}
            </div>
            {!isNormalized && (
              <button
                onClick={onNormalizeCategory}
                className="text-[10px] font-semibold text-[#FF6E4C] hover:text-[#FF6E4C]/80 
                           px-2 py-1 rounded border border-[#FF6E4C]/30 hover:bg-[#FF6E4C]/5 transition-colors"
              >
                Normalize to 100%
              </button>
            )}
          </div>
          {measures.map((measure) => (
            <MeasureSlider
              key={measure.key}
              measureKey={measure.key}
              definition={measure}
              value={measureWeights[measure.key] || measure.defaultWeight}
              onChange={(value) => onMeasureChange(measure.key, value)}
              isDefault={measureWeights[measure.key] === defaultMeasureWeights[measure.key]}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================
// MAIN PAGE COMPONENT
// ============================================================

export default function EAWeightsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState | null;
  
  const {
    categoryWeights: savedCategoryWeights,
    updateCategoryWeight,
    resetCategoryWeights,
    areCategoryWeightsCustomized,
    measureWeights: savedMeasureWeights,
    updateMeasureWeight,
    resetMeasureWeights,
    resetAllMeasureWeights,
    areMeasureWeightsCustomized,
  } = useEA();

  // Local state for editing (allows manual entry without auto-rebalancing)
  const [localCategoryWeights, setLocalCategoryWeights] = useState<Record<string, number>>(savedCategoryWeights);
  const [localMeasureWeights, setLocalMeasureWeights] = useState<Record<string, Record<string, number>>>(savedMeasureWeights);

  // Sync local state when saved state changes (e.g., after reset)
  useEffect(() => {
    setLocalCategoryWeights(savedCategoryWeights);
  }, [savedCategoryWeights]);

  useEffect(() => {
    setLocalMeasureWeights(savedMeasureWeights);
  }, [savedMeasureWeights]);

  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>('categories');
  
  // Expanded categories for measure weights
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(['costSavings']));

  // Get default weights for comparison
  const defaultCategoryWeights = getDefaultCategoryWeightsMap();
  const defaultMeasureWeights = getDefaultMeasureWeightsMap();

  // Calculate totals
  const categoryTotal = Object.values(localCategoryWeights).reduce((a, b) => a + b, 0);
  const isCategoryNormalized = Math.abs(categoryTotal - 100) < 0.5;

  // Toggle category expansion
  const toggleCategory = (categoryKey: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryKey)) {
        next.delete(categoryKey);
      } else {
        next.add(categoryKey);
      }
      return next;
    });
  };

  // Handle back navigation with scroll restoration
  const handleBack = () => {
    const returnTo = state?.returnTo || '/opportunities';
    const restoreScrollY = state?.restoreScrollY;
    
    navigate(returnTo, {
      state: restoreScrollY !== undefined ? { restoreScrollY } : undefined,
    });
  };

  // Category weight handlers (manual - no auto-rebalance)
  const handleCategoryWeightChange = (key: string, value: number) => {
    const newWeights = { ...localCategoryWeights, [key]: value };
    setLocalCategoryWeights(newWeights);
    // Save to context immediately (user can normalize later)
    updateCategoryWeight(key, value);
  };

  const handleNormalizeCategoryWeights = () => {
    const normalized = normalizeWeights(localCategoryWeights);
    setLocalCategoryWeights(normalized);
    // Save all normalized weights to context
    Object.entries(normalized).forEach(([key, value]) => {
      updateCategoryWeight(key, value);
    });
  };

  const handleResetSingleCategory = (categoryKey: string) => {
    const defaultValue = defaultCategoryWeights[categoryKey];
    handleCategoryWeightChange(categoryKey, defaultValue);
  };

  const handleResetAllCategoryWeights = () => {
    resetCategoryWeights();
    setLocalCategoryWeights(getDefaultCategoryWeightsMap());
  };

  // Measure weight handlers (manual - no auto-rebalance)
  const handleMeasureWeightChange = (categoryKey: string, measureKey: string, value: number) => {
    const newCategoryMeasures = { 
      ...localMeasureWeights[categoryKey], 
      [measureKey]: value 
    };
    const newWeights = { 
      ...localMeasureWeights, 
      [categoryKey]: newCategoryMeasures 
    };
    setLocalMeasureWeights(newWeights);
    // Save to context immediately
    updateMeasureWeight(categoryKey, measureKey, value);
  };

  const handleNormalizeMeasureWeights = (categoryKey: string) => {
    const categoryMeasures = localMeasureWeights[categoryKey] || {};
    const normalized = normalizeWeights(categoryMeasures);
    const newWeights = { ...localMeasureWeights, [categoryKey]: normalized };
    setLocalMeasureWeights(newWeights);
    // Save all normalized weights to context
    Object.entries(normalized).forEach(([measureKey, value]) => {
      updateMeasureWeight(categoryKey, measureKey, value);
    });
  };

  const handleResetCategoryMeasures = (categoryKey: string) => {
    resetMeasureWeights(categoryKey);
    setLocalMeasureWeights(prev => ({
      ...prev,
      [categoryKey]: defaultMeasureWeights[categoryKey]
    }));
  };

  const handleResetAllMeasureWeights = () => {
    resetAllMeasureWeights();
    setLocalMeasureWeights(getDefaultMeasureWeightsMap());
  };

  // Check if single category is default
  const isCategoryDefault = (categoryKey: string) => {
    return localCategoryWeights[categoryKey] === defaultCategoryWeights[categoryKey];
  };

  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      {/* Header - Compact */}
      <div className="sticky top-0 z-10 bg-white border-b border-[#e5e7eb] shadow-sm">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={handleBack}
                className="flex items-center gap-1.5 text-sm font-medium text-[#717182] hover:text-[#18365E] transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
              <div className="w-px h-5 bg-[#e5e7eb]" />
              <div>
                <h1 className="text-base font-bold text-[#18365E]">Weight Settings</h1>
                <p className="text-[11px] text-[#717182]">
                  Fine-tune how DVA ranks opportunities for your organization. Changes apply immediately.
                </p>
              </div>
            </div>
            
            {/* Reset All Button */}
            {(areCategoryWeightsCustomized || areMeasureWeightsCustomized) && (
              <button
                onClick={() => {
                  handleResetAllCategoryWeights();
                  handleResetAllMeasureWeights();
                }}
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-[#717182] hover:text-[#18365E] hover:bg-[#F5F7FA] rounded-lg transition-colors"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                Reset All to Defaults
              </button>
            )}
          </div>
        </div>
        
        {/* Tab Navigation */}
        <div className="px-4 flex gap-1">
          <button
            onClick={() => setActiveTab('categories')}
            className={`flex items-center gap-2 px-3 py-2 text-sm font-semibold rounded-t-lg transition-colors ${
              activeTab === 'categories'
                ? 'bg-[#F5F7FA] text-[#18365E] border-t border-x border-[#e5e7eb]'
                : 'text-[#717182] hover:text-[#18365E] hover:bg-[#F5F7FA]/50'
            }`}
          >
            <Scale className="w-4 h-4" />
            Category Weights
            {areCategoryWeightsCustomized && (
              <span className="w-2 h-2 rounded-full bg-amber-500" />
            )}
          </button>
          <button
            onClick={() => setActiveTab('measures')}
            className={`flex items-center gap-2 px-3 py-2 text-sm font-semibold rounded-t-lg transition-colors ${
              activeTab === 'measures'
                ? 'bg-[#F5F7FA] text-[#18365E] border-t border-x border-[#e5e7eb]'
                : 'text-[#717182] hover:text-[#18365E] hover:bg-[#F5F7FA]/50'
            }`}
          >
            <Layers className="w-4 h-4" />
            Measure Weights
            {areMeasureWeightsCustomized && (
              <span className="w-2 h-2 rounded-full bg-amber-500" />
            )}
          </button>
        </div>
      </div>

      {/* Main Content - Full Width with minimal gutters */}
      <main className="px-4 py-4">
        {/* Category Weights Tab */}
        {activeTab === 'categories' && (
          <div className="max-w-7xl mx-auto">
            {/* Header Row with Help */}
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-[#18365E]">Category Weights</span>
                <HelpPopover helpKey="category_weights" />
              </div>
              <div className="flex items-center gap-3">
                {/* Total Indicator */}
                {isCategoryNormalized ? (
                  <span className="text-sm font-bold text-green-600 flex items-center gap-1">
                    <Check className="w-4 h-4" />
                    Total: {Math.round(categoryTotal)}%
                  </span>
                ) : (
                  <span className="text-sm font-bold text-amber-600 flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4" />
                    Total: {Math.round(categoryTotal)}%
                  </span>
                )}
                
                {/* Normalize Button */}
                {!isCategoryNormalized && (
                  <button
                    onClick={handleNormalizeCategoryWeights}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white 
                               bg-[#FF6E4C] hover:bg-[#FF6E4C]/90 rounded-lg transition-colors shadow-sm"
                  >
                    Normalize to 100%
                  </button>
                )}
                
                {/* Reset */}
                {areCategoryWeightsCustomized && (
                  <button
                    onClick={handleResetAllCategoryWeights}
                    className="text-xs font-medium text-[#717182] hover:text-[#18365E] transition-colors"
                  >
                    Reset All
                  </button>
                )}
              </div>
            </div>

            {/* Category Sliders Grid - 3 columns on large screens */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {Object.entries(CATEGORY_DEFINITIONS).map(([key, definition]) => (
                <CategorySlider
                  key={key}
                  categoryKey={key}
                  definition={definition}
                  value={localCategoryWeights[key] || definition.defaultWeight}
                  onChange={handleCategoryWeightChange}
                  onReset={() => handleResetSingleCategory(key)}
                  isDefault={isCategoryDefault(key)}
                />
              ))}
            </div>

            {/* Dimension Legend - Compact */}
            <div className="mt-4 p-3 bg-white rounded-lg border border-[#e5e7eb]">
              <div className="flex flex-wrap items-center gap-6">
                <span className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide">Dimensions:</span>
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded bg-[#18365E]/30" />
                  <span className="text-xs text-[#717182]">
                    <strong className="text-[#18365E]">Business Value</strong> — Financial impact, strategic fit, speed to results
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded bg-[#FF6E4C]/30" />
                  <span className="text-xs text-[#717182]">
                    <strong className="text-[#FF6E4C]">Ease of Implementation</strong> — Data readiness, compliance, organizational capacity
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Measure Weights Tab */}
        {activeTab === 'measures' && (
          <div className="max-w-7xl mx-auto">
            {/* Header Row with Help */}
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-[#18365E]">Measure Weights by Category</span>
                <HelpPopover helpKey="measure_weights" />
              </div>
              {areMeasureWeightsCustomized && (
                <button
                  onClick={handleResetAllMeasureWeights}
                  className="text-xs font-medium text-[#FF6E4C] hover:text-[#FF6E4C]/80 transition-colors"
                >
                  Reset All Measures
                </button>
              )}
            </div>

            {/* Category Sections - 2 columns on large screens */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {Object.entries(CATEGORY_DEFINITIONS).map(([categoryKey, definition]) => {
                const measures = MEASURE_DEFINITIONS[categoryKey] || [];
                const catMeasureWeights = localMeasureWeights[categoryKey] || {};
                const defaultCatMeasureWeights = defaultMeasureWeights[categoryKey] || {};
                
                return (
                  <CategoryMeasuresSection
                    key={categoryKey}
                    categoryKey={categoryKey}
                    definition={definition}
                    measures={measures}
                    measureWeights={catMeasureWeights}
                    defaultMeasureWeights={defaultCatMeasureWeights}
                    onMeasureChange={(measureKey, value) => 
                      handleMeasureWeightChange(categoryKey, measureKey, value)
                    }
                    onNormalizeCategory={() => handleNormalizeMeasureWeights(categoryKey)}
                    onResetCategory={() => handleResetCategoryMeasures(categoryKey)}
                    isExpanded={expandedCategories.has(categoryKey)}
                    onToggle={() => toggleCategory(categoryKey)}
                  />
                );
              })}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}